import { JSONSchema7Definition } from 'json-schema';
import _ from 'lodash';
import { iconWordSpacingSvg, iconLineHeightSvg, iconTextIndentSvg } from '../assets/images';
import {
    TextBlendModeOptions,
    FontWeightOptions,
    TextDecorationOptions,
} from './cssPropertyOptions';

declare module 'json-schema' {
    export interface JSONSchema7 {
        enumNames?: Array<string | number>;
    }
}

export interface TextSchemaWidgetProperties {
    opacity?: string;
    blendMode?: string;
    decoration?: string;
    fontFamily?: string;
    fontWeight?: string;
    wordSpacing?: string;
    lineHeight?: string;
    textIndent?: string;
    color?: string;
    fontSize?: string;
    horizontalAlignment?: string;
    verticalAlignment?: string;
    shadow?: string;
}

export interface TextSchemaProperties {
    opacity: string;
    'mix-blend-mode': string;
    'text-decoration': string;
    'font-family': string;
    'font-weight': string;
    'word-spacing': string;
    'line-height': string;
    'text-indent': string;
    color: string;
    'font-size': string;
    'text-align': string;
    'vertical-align': string;
    'text-shadow': string;
}

interface Schema {
    [key: string]: JSONSchema7Definition;
}

const schema: Schema = {
    // related css property-> opacity: value;
    opacity: {
        type: 'string',
        title: 'Opacity',
        default: '',
    },
    // related css property-> mix-blend-mode: value;
    blendMode: {
        type: 'string',
        title: 'Blend Mode',
        enum: _.keys(TextBlendModeOptions),
        enumNames: _.values(TextBlendModeOptions),
        default: 'normal',
    },
    // related css property-> text-decoration: value;
    decoration: {
        type: 'string',
        title: 'Decoration',
        enum: _.keys(TextDecorationOptions),
        enumNames: _.values(TextDecorationOptions),
        default: 'none',
    },
    // related css property-> font-family: value;
    fontFamily: {
        type: 'string',
        title: 'Font Family',
        default: '',
    },
    // related css property-> font-weight: value;
    fontWeight: {
        type: 'string',
        title: 'Font Weight',
        enum: _.keys(FontWeightOptions),
        enumNames: _.values(FontWeightOptions),
        default: 'normal',
    },
    // related css property-> word-spacing: value(unit);
    wordSpacing: {
        type: 'string',
        title: 'Word Spacing',
        default: '',
    },
    // related css property-> lineHeight: value(unit);
    lineHeight: {
        type: 'string',
        title: 'Line Height',
        default: '',
    },
    // TODO: related css property-> text-indent: value(unit);
    textIndent: {
        type: 'string',
        title: 'Text Indent',
        default: '',
    },
    // related css property-> color: value;
    color: {
        type: 'string',
        title: 'Color',
        default: '',
    },
    // related css property-> font-size: value(unit);
    fontSize: {
        type: 'string',
        title: 'Font Size',
        default: '',
    },
    // related css property-> text-align: value;
    horizontalAlignment: {
        type: 'string',
        default: '',
    },
    // related css property-> vertical-align: value;
    verticalAlignment: {
        type: 'string',
        default: '',
    },
    // related css property-> text-shadow: xValue(unit) yValue(unit) rbga(val, val, val, opacity);
    shadow: {
        type: 'string',
        title: 'Text Shadow',
        default: '',
    },
    typographyClass: {
        type: 'string',
        title: 'Select TypoGraphy Class',
        default: 'Default',
    },
};

const group = {
    Text: [
        'opacity',
        'blendMode',
        'decoration',
        'fontFamily',
        'fontWeight',
        'wordSpacing',
        'lineHeight',
        'textIndent',
        'color',
        'fontSize',
        'horizontalAlignment',
        'verticalAlignment',
        'shadow',
        'typographyClass',
    ],
};

const uiSchema = {
    blendMode: { 'ui:widget': 'CustomSelect' },
    decoration: { 'ui:widget': 'CustomSelect' },
    fontFamily: { 'ui:widget': 'FontFamilySelect' },
    fontWeight: { 'ui:widget': 'CustomSelect' },
    wordSpacing: {
        'ui:widget': 'UnitSelectorWithIcon',
        'ui:options': { icon: iconWordSpacingSvg },
    },
    lineHeight: { 'ui:widget': 'UnitSelectorWithIcon', 'ui:options': { icon: iconLineHeightSvg } },
    textIndent: { 'ui:widget': 'UnitSelectorWithIcon', 'ui:options': { icon: iconTextIndentSvg } },
    fontSize: { 'ui:widget': 'UnitSelectorWidget' },
    verticalAlignment: { 'ui:widget': 'VerticalAlignment' },
    horizontalAlignment: { 'ui:widget': 'HorizontalAlignment' },
    color: { 'ui:widget': 'ThemeColorPicker' },
    opacity: { 'ui:widget': 'Slider' },
    shadow: { 'ui:widget': 'Shadow' },
    typographyClass: {
        'ui:widget': 'TypographyClassSelect',
    },
};

export const TextSchema = {
    schema,
    group,
    uiSchema,
};
